import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Edit2, Lock, X, Trash2, Eye, CheckCircle2, Clock, XCircle, AlertTriangle, CalendarClock } from "lucide-react";
import { SearchableSelect } from "@/components/SearchableSelect";
import { toast } from "sonner";

const ROLES = ["doctor", "optometrist", "manager", "paramedic", "salesperson", "registrar"];
const ROLE_L = { doctor: "Лікар", optometrist: "Оптометрист", manager: "Менеджер", paramedic: "Фельдшер", salesperson: "Фахівець оптики", registrar: "Реєстратор" };

const LIFECYCLES = [
  { value: "new", label: "Нова" },
  { value: "thinking", label: "Думає" },
  { value: "collecting", label: "Збирає кошти" },
  { value: "callback", label: "Передзвонити" },
  { value: "scheduled", label: "Записаний" },
  { value: "operated", label: "Прооперований" },
  { value: "refused", label: "Відмовився" },
  { value: "irrelevant", label: "Неактуальна" },
];
const LIFECYCLE_BADGE = {
  new: "bg-slate-100 text-slate-700",
  thinking: "bg-amber-50 text-amber-700",
  collecting: "bg-orange-50 text-orange-700",
  callback: "bg-sky-50 text-sky-700",
  scheduled: "bg-violet-50 text-violet-700",
  operated: "bg-emerald-50 text-emerald-700",
  refused: "bg-rose-50 text-rose-700",
  irrelevant: "bg-slate-100 text-slate-500",
};

// Iteration 28: execution status visuals
const EXEC_BADGE = {
  not_started: "bg-slate-100 text-slate-700",
  scheduled: "bg-amber-50 text-amber-800",
  partial: "bg-orange-50 text-orange-800",
  completed: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-slate-100 text-slate-500",
  expired: "bg-rose-50 text-rose-700",
};
const EXEC_ICON = {
  not_started: Clock,
  scheduled: CalendarClock,
  partial: AlertTriangle,
  completed: CheckCircle2,
  cancelled: XCircle,
  expired: AlertTriangle,
};
const ITEM_BADGE = {
  not_started: "bg-slate-100 text-slate-700",
  scheduled: "bg-amber-50 text-amber-800",
  transferred: "bg-sky-50 text-sky-800",
  completed: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-slate-100 text-slate-500",
  expired: "bg-rose-50 text-rose-700",
};

const EMPTY_ITEM = {
  procedure_id: "", eye: "",
  estimated_amount: 0, comment: "",
};
const EMPTY = {
  patient_id: "", diagnosis: "", items: [{ ...EMPTY_ITEM }],
  patient_type: "primary",
  referral_source: "", external_doctor_id: "",
  clinic_team: [], business_trip_team: [], business_trip_comment: "",
  comment: "", recommended_text: "",
  lifecycle_status: "new", manager_id: "", call_date: "", last_comment: "",
};

export default function Recommendations() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [patients, setPatients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [sources, setSources] = useState([]);
  const [extDoctors, setExtDoctors] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [lifecycleFilter, setLifecycleFilter] = useState("all");
  const [executionFilter, setExecutionFilter] = useState("all");   // Iteration 28
  const [pendingEyeOnly, setPendingEyeOnly] = useState(false);     // Iteration 28
  const [detailRec, setDetailRec] = useState(null);                // Iteration 28: view dialog
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [user, setUser] = useState(null);
  const [saving, setSaving] = useState(false);  // Iteration 22: block double-submit

  // Iteration 22: reserved marketing source label — verbatim from backend seed_catalog.py
  const EXTERNAL_MARKETING_LABEL = "Направлення зовнішніх лікарів";

  const load = async () => {
    // Iteration 31: use allSettled so a single permission-denied sub-request
    // (e.g. /patients when patients.read override excluded the role) does not
    // blank out the entire Recommendations page.
    const results = await Promise.allSettled([
      api.get("/recommendations"),
      api.get("/patients"),
      api.get("/employees"),
      api.get("/catalog/procedures?include_inactive=false"),
      api.get("/catalog/referral-sources?include_inactive=false"),
      api.get("/catalog/external-doctors?include_inactive=false"),
      api.get("/auth/me"),
    ]);
    const [r, p, e, pr, sr, ed, u] = results;
    if (r.status === "fulfilled") setItems(r.value.data);
    else { toast.error(formatApiError(r.reason?.response?.data?.detail) || "Не вдалося завантажити рекомендації"); setItems([]); }
    setPatients(p.status === "fulfilled" ? p.value.data : []);
    if (e.status === "fulfilled") setEmployees(e.value.data);
    else { setEmployees([]); toast.error("Немає доступу до працівників (employees.read) — списки персоналу порожні"); }
    setProcedures(pr.status === "fulfilled" ? pr.value.data : []);
    setSources(sr.status === "fulfilled" ? sr.value.data : []);
    if (ed.status === "fulfilled") setExtDoctors(ed.value.data);
    else { setExtDoctors([]); toast.error("Немає доступу до довідника зовнішніх лікарів (catalog.read)"); }
    if (u.status === "fulfilled") setUser(u.value.data);
  };
  useEffect(() => { load(); }, []);

  // Auto-open create dialog when navigated from PatientDetail (?create=1&patient_id=...)
  const [autoLockedPatient, setAutoLockedPatient] = useState(false);
  useEffect(() => {
    if (!patients.length) return;
    if (searchParams.get("create") === "1") {
      const pid = searchParams.get("patient_id") || "";
      const pat = patients.find((p) => p.id === pid);
      setEditing(null);
      // Iteration 22: auto-fill referral source when patient's marketing source is
      // "Направлення зовнішніх лікарів" — copy the external doctor id from patient card.
      let initial = { ...EMPTY, items: [{ ...EMPTY_ITEM }], patient_id: pat ? pid : "" };
      if (pat && pat.marketing_source === EXTERNAL_MARKETING_LABEL && pat.marketing_external_doctor_id) {
        initial = {
          ...initial,
          patient_type: "referred",
          referral_source: "external_doctor",
          external_doctor_id: pat.marketing_external_doctor_id,
        };
      }
      setForm(initial);
      setAutoLockedPatient(!!pat);
      setOpen(true);
      // Clear the URL params so reload doesn't re-trigger
      const next = new URLSearchParams(searchParams);
      next.delete("create"); next.delete("patient_id");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patients]);

  // Iteration 22: when user picks a patient in the create form, auto-fill referral
  // source & external doctor from the patient's marketing preferences.
  useEffect(() => {
    if (editing) return; // only on create
    if (!form.patient_id) return;
    const pat = patients.find((p) => p.id === form.patient_id);
    if (!pat) return;
    if (pat.marketing_source === EXTERNAL_MARKETING_LABEL && pat.marketing_external_doctor_id) {
      // Only overwrite if fields are still empty (user hasn't manually tweaked yet)
      setForm((prev) => (
        prev.referral_source ? prev : {
          ...prev,
          patient_type: "referred",
          referral_source: "external_doctor",
          external_doctor_id: pat.marketing_external_doctor_id,
        }
      ));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.patient_id, patients]);

  const srcByKey = useMemo(() => Object.fromEntries(sources.map((s) => [s.key, s])), [sources]);
  const currentSrc = srcByKey[form.referral_source];
  const isReferred = form.patient_type === "referred";
  const currentSrcNeedsTeam = !!currentSrc?.requires_team && isReferred;
  const currentSrcIsExtDoctor = currentSrc?.source_type === "external_doctor" && isReferred;

  const buildEmpByRole = (group) => {
    const m = {};
    employees
      .filter((e) => e.is_active !== false && (e.groups || ["clinic"]).includes(group))
      .forEach((e) => { (m[e.role] = m[e.role] || []).push(e); });
    return m;
  };
  const empByRoleClinic = useMemo(() => buildEmpByRole("clinic"), [employees]); // eslint-disable-line react-hooks/exhaustive-deps
  const empByRoleTrip = useMemo(() => buildEmpByRole("trip"), [employees]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = items
    .filter((i) => statusFilter === "all" || i.status === statusFilter)
    .filter((i) => lifecycleFilter === "all" || i.lifecycle_status === lifecycleFilter)
    .filter((i) => executionFilter === "all" || (i.execution?.status || "not_started") === executionFilter)
    .filter((i) => !pendingEyeOnly || i.execution?.pending_second_eye);

  const save = async () => {
    if (saving) return;  // Iteration 22: prevent double-click duplicates
    if (!form.diagnosis?.trim()) { toast.error("Заповніть поле «Діагноз»"); return; }
    if (!form.items?.length || form.items.some((i) => !i.procedure_id)) {
      toast.error("Додайте щонайменше один рядок «Рекомендовано» з обраною процедурою"); return;
    }
    if (isReferred) {
      if (!form.referral_source) { toast.error("Оберіть джерело направлення"); return; }
      if (currentSrcIsExtDoctor && !form.external_doctor_id) { toast.error("Оберіть зовнішнього лікаря"); return; }
      if (currentSrcNeedsTeam && !form.business_trip_team?.length) {
        toast.error("Це джерело направлення вимагає вказати бригаду направлення"); return;
      }
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (form.patient_type === "primary") {
        payload.referral_source = "";
        payload.external_doctor_id = "";
        payload.business_trip_team = [];
      } else {
        if (!currentSrcNeedsTeam) payload.business_trip_team = [];
        if (!currentSrcIsExtDoctor) payload.external_doctor_id = "";
      }
      if (editing) await api.put(`/recommendations/${editing.id}`, payload);
      else await api.post("/recommendations", payload);
      toast.success("Збережено"); setOpen(false); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const closeRec = async (it) => {
    if (!window.confirm("Закрити рекомендацію? Це знімає блокування дублювань.")) return;
    try { await api.post(`/recommendations/${it.id}/close`); toast.success("Закрито"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteRec = async (it) => {
    if (!window.confirm(`Видалити рекомендацію назавжди?\n\nЦе незворотна дія. Якщо до рекомендації прив'язані ПРОВЕДЕНІ операції — видалення буде заблоковано.`)) return;
    try { await api.delete(`/recommendations/${it.id}`); toast.success("Рекомендацію видалено"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const canDelete = !!user && (user.role === "admin" || user.role === "director");
  const startCreate = () => { setEditing(null); setForm({ ...EMPTY, items: [{ ...EMPTY_ITEM }] }); setOpen(true); };
  const startEdit = (it) => {
    setEditing(it);
    setForm({
      ...EMPTY,
      ...it,
      patient_type: it.patient_type || (it.referral_source && it.referral_source !== "self" ? "referred" : "primary"),
      referral_source: it.referral_source || "",
      external_doctor_id: it.external_doctor_id || "",
      items: (it.items && it.items.length) ? it.items : [{ ...EMPTY_ITEM, procedure_id: "", eye: it.eye || "" }],
    });
    setOpen(true);
  };
  const updItem = (idx, field, val) => {
    const next = [...form.items]; next[idx] = { ...next[idx], [field]: val };
    setForm({ ...form, items: next });
  };
  const addItem = () => setForm({ ...form, items: [...form.items, { ...EMPTY_ITEM }] });
  const rmItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  const updTeam = (key, idx, field, val) => {
    const next = [...form[key]]; next[idx] = { ...next[idx], [field]: val };
    setForm({ ...form, [key]: next });
  };
  const addTeam = (key) => setForm({ ...form, [key]: [...form[key], { employee_id: "", role: "doctor", share_percent: 100 }] });
  const rmTeam = (key, idx) => setForm({ ...form, [key]: form[key].filter((_, i) => i !== idx) });

  return (
    <div data-testid="recommendations-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading text-3xl font-bold text-[#0F2027]">Рекомендації</h1>
          <p className="text-sm text-slate-500">{items.length} рекомендацій · діє 3 місяці</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="rec-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Усі (термін дії)</SelectItem>
              <SelectItem value="active">Активні</SelectItem>
              <SelectItem value="expired">Прострочені</SelectItem>
            </SelectContent>
          </Select>
          <Select value={lifecycleFilter} onValueChange={setLifecycleFilter}>
            <SelectTrigger className="w-44" data-testid="rec-filter-lifecycle"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Усі статуси</SelectItem>
              {LIFECYCLES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={executionFilter} onValueChange={setExecutionFilter}>
            <SelectTrigger className="w-48" data-testid="rec-filter-execution"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Виконання: усі</SelectItem>
              <SelectItem value="not_started">Не виконано</SelectItem>
              <SelectItem value="scheduled">Заплановано</SelectItem>
              <SelectItem value="partial">Виконано частково</SelectItem>
              <SelectItem value="completed">Виконано повністю</SelectItem>
              <SelectItem value="expired">Протерміновано</SelectItem>
              <SelectItem value="cancelled">Скасовано</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant={pendingEyeOnly ? "default" : "outline"}
            className={pendingEyeOnly ? "bg-[#0D9488] hover:bg-[#0D9488]/90" : ""}
            onClick={() => setPendingEyeOnly((v) => !v)}
            data-testid="rec-filter-pending-eye"
            title="Показати рекомендації з невиконаною операцією на другому оці"
          >
            <Eye className="h-4 w-4 mr-1" /> Не виконане 2-е око
          </Button>
          <Button onClick={startCreate} data-testid="add-rec" className="bg-[#0F2027] hover:bg-[#0F2027]/90">
            <Plus className="h-4 w-4 mr-1" />Створити
          </Button>
        </div>
      </div>

      <Card className="gv-card"><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600 sticky top-0 z-10">
            <tr>
              <th className="text-left p-3">Дата</th>
              <th className="text-left p-3">Пацієнт</th>
              <th className="text-left p-3">Операція</th>
              <th className="text-left p-3">Око</th>
              <th className="text-left p-3">Тип / Джерело</th>
              <th className="text-left p-3">Статус</th>
              <th className="text-left p-3">Виконання</th>
              <th className="text-left p-3">Дзвінок</th>
              <th className="text-left p-3">Менеджер</th>
              <th className="text-center p-3">Термін</th>
              <th className="text-right p-3 w-36">Дії</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const pat = patients.find((p) => p.id === r.patient_id);
              const src = srcByKey[r.referral_source];
              const mgr = employees.find((e) => e.id === r.manager_id);
              const recItems = r.items && r.items.length ? r.items : [{ procedure_name: r.operation_type, eye: r.eye }];
              return (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`rec-row-${r.id}`}>
                  <td className="p-3 text-xs">{(r.created_at || "").slice(0, 10)}</td>
                  <td className="p-3 font-medium">
                    <div>{pat?.full_name || "—"}</div>
                    {r.diagnosis && <div className="text-xs text-slate-500 mt-0.5">Діагноз: {r.diagnosis}</div>}
                  </td>
                  <td className="p-3 text-xs" colSpan={2}>
                    {recItems.map((it, i) => (
                      <div key={i}>· {it.procedure_name || "—"} {it.eye && <Badge variant="outline" className="ml-1 text-[10px]">{it.eye}</Badge>}</div>
                    ))}
                  </td>
                  <td className="p-3 text-xs">
                    {r.patient_type === "primary" ? (
                      <Badge className="bg-sky-50 text-sky-700">Первинний</Badge>
                    ) : (
                      <div className="flex flex-col gap-0.5">
                        <Badge className="bg-violet-50 text-violet-700 w-fit">Направлений</Badge>
                        <span className="text-slate-500">{src?.label || r.referral_source || "—"}</span>
                        {r.external_doctor_name && <span className="text-slate-400 italic">{r.external_doctor_name}</span>}
                      </div>
                    )}
                  </td>
                  <td className="p-3"><Badge data-testid={`rec-status-${r.id}`} className={`${LIFECYCLE_BADGE[r.lifecycle_status] || "bg-slate-100 text-slate-700"} hover:${LIFECYCLE_BADGE[r.lifecycle_status]}`}>{r.lifecycle_label}</Badge></td>
                  <td className="p-3">
                    {(() => {
                      const ex = r.execution || { status: "not_started", done_count: 0, total_count: 0 };
                      const ExIcon = EXEC_ICON[ex.status] || Clock;
                      return (
                        <div className="flex items-center gap-1.5">
                          <Badge className={`${EXEC_BADGE[ex.status]} hover:${EXEC_BADGE[ex.status]} inline-flex items-center gap-1`}
                                  data-testid={`rec-execution-${r.id}`}>
                            <ExIcon className="h-3 w-3" />
                            {ex.total_count > 0 ? `${ex.done_count}/${ex.total_count}` : "—"}
                          </Badge>
                          {ex.pending_second_eye && (
                            <span title="Очікує операції другого ока" data-testid={`rec-pending-eye-${r.id}`}>
                              <Eye className="h-4 w-4 text-[#0D9488]" />
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="p-3 text-xs">{r.call_date || "—"}</td>
                  <td className="p-3 text-xs">{mgr?.full_name || "—"}</td>
                  <td className="p-3 text-center">
                    {r.closed ? <Badge className="bg-slate-100 text-slate-500">Закрита</Badge>
                     : r.status === "active" ? (
                        <div className="flex flex-col items-center">
                          <Badge className="bg-emerald-50 text-emerald-700">Активна</Badge>
                          {typeof r.execution?.days_left === "number" && r.execution.days_left > 0 && (
                            <span className="text-[10px] text-slate-500 mt-0.5">залишилось {r.execution.days_left} дн.</span>
                          )}
                        </div>
                     )
                     : <Badge className="bg-amber-50 text-amber-700">Прострочена</Badge>}
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex gap-1 justify-end">
                      <Button size="icon" variant="ghost" onClick={() => setDetailRec(r)} data-testid={`view-rec-${r.id}`} title="Деталі"><Eye className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => startEdit(r)} data-testid={`edit-rec-${r.id}`}><Edit2 className="h-4 w-4" /></Button>
                      {!r.closed && (user?.role === "admin" || user?.role === "director") && (
                        <Button size="icon" variant="ghost" onClick={() => closeRec(r)} data-testid={`close-rec-${r.id}`}><Lock className="h-4 w-4 text-amber-600" /></Button>
                      )}
                      {canDelete && (
                        <Button size="icon" variant="ghost" onClick={() => deleteRec(r)} data-testid={`delete-rec-${r.id}`} title="Видалити рекомендацію (директор/адмін)"><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={11} className="p-8 text-center text-slate-400">Немає рекомендацій</td></tr>}
          </tbody>
        </table>
      </CardContent></Card>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setAutoLockedPatient(false); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Редагувати рекомендацію" : "Нова рекомендація"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-3">
              <Label>Пацієнт <span className="text-rose-500">*</span></Label>
              {autoLockedPatient ? (
                <Input
                  data-testid="rec-patient-locked"
                  value={patients.find((p) => p.id === form.patient_id)?.full_name || ""}
                  disabled
                  className="bg-slate-50"
                />
              ) : (
                <SearchableSelect
                  value={form.patient_id}
                  onChange={(v) => setForm({ ...form, patient_id: v })}
                  options={patients.map((p) => ({
                    value: p.id, label: p.full_name,
                    hint: [p.phone, p.phone_alt].filter(Boolean).join(" · "),
                    keywords: [p.full_name, p.phone, p.phone_alt].filter(Boolean).join(" "),
                  }))}
                  placeholder="Оберіть пацієнта"
                  empty="Пацієнтів не знайдено"
                  testid="rec-patient"
                />
              )}
              {autoLockedPatient && <div className="text-xs text-slate-500 mt-1">Підставлено автоматично з картки пацієнта.</div>}
            </div>

            {/* Recommended items repeater */}
            <div className="md:col-span-3 mt-2 p-3 border border-slate-200 rounded-md bg-slate-50/50">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold text-[#0F2027]">Рекомендовано <span className="text-rose-500">*</span></Label>
                <Button size="sm" variant="outline" onClick={addItem} data-testid="add-rec-item"><Plus className="h-3 w-3 mr-1" />Додати рядок</Button>
              </div>
                {form.items.map((it, idx) => {
                  const proc = procedures.find((p) => p.id === it.procedure_id);
                  const derivedEye = proc?.name?.match(/\b(OD|OS)\b/i)?.[1]?.toUpperCase() || "";
                  return (
                    <div key={idx} className="grid grid-cols-1 md:grid-cols-[2fr_140px_40px] gap-2 items-end p-2 mb-2 bg-white border border-slate-200 rounded-md" data-testid={`rec-item-${idx}`}>
                      <div>
                        <Label className="text-xs">Процедура {derivedEye && <Badge variant="outline" className="ml-1 text-[10px]">{derivedEye}</Badge>}</Label>
                        <Select value={it.procedure_id} onValueChange={(v) => updItem(idx, "procedure_id", v)}>
                          <SelectTrigger data-testid={`item-proc-${idx}`}><SelectValue placeholder="Оберіть" /></SelectTrigger>
                          <SelectContent className="max-h-80">
                            {procedures.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Орієнтовна сума</Label>
                        <Input type="number" data-testid={`item-amount-${idx}`} placeholder="0" value={it.estimated_amount ? it.estimated_amount : ""} onChange={(e) => updItem(idx, "estimated_amount", e.target.value === "" ? 0 : parseFloat(e.target.value))} />
                      </div>
                      <Button size="icon" variant="ghost" onClick={() => rmItem(idx)} disabled={form.items.length === 1}><X className="h-4 w-4 text-red-500" /></Button>
                      <div className="md:col-span-3 -mt-1 pl-1 flex items-center gap-3">
                        {proc?.category && <span className="text-xs text-slate-500">Категорія: {proc.category_label || proc.category}</span>}
                        <Input className="h-8 flex-1" data-testid={`item-comment-${idx}`} value={it.comment || ""} onChange={(e) => updItem(idx, "comment", e.target.value)} placeholder="Коментар до рядка (необов'язково)" />
                      </div>
                    </div>
                  );
                })}
              {form.items.length === 0 && <div className="text-sm text-slate-400">Додайте перший рядок</div>}
            </div>

            <div>
              <Label>Тип пацієнта <span className="text-rose-500">*</span></Label>
              <Select value={form.patient_type} onValueChange={(v) => setForm({ ...form, patient_type: v, referral_source: v === "primary" ? "" : form.referral_source, external_doctor_id: v === "primary" ? "" : form.external_doctor_id, business_trip_team: v === "primary" ? [] : form.business_trip_team })}>
                <SelectTrigger data-testid="rec-patient-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="primary">Первинний</SelectItem>
                  <SelectItem value="referred">Направлений</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-xs text-slate-500 mt-1">Первинний — без джерела/бригади. Направлений — потрібне джерело.</div>
            </div>
            {isReferred && (
              <div className="md:col-span-2">
                <Label>Джерело направлення <span className="text-rose-500">*</span></Label>
                <Select value={form.referral_source} onValueChange={(v) => setForm({ ...form, referral_source: v, external_doctor_id: "", business_trip_team: [] })}>
                  <SelectTrigger data-testid="rec-source"><SelectValue placeholder="Оберіть джерело" /></SelectTrigger>
                  <SelectContent>
                    {sources.map((s) => (
                      <SelectItem key={s.key} value={s.key}>
                        {s.label}
                        {s.source_type === "external_doctor" ? " · зовн. лікар" : ""}
                        {s.requires_team ? " · потребує бригаду" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {isReferred && currentSrcIsExtDoctor && (
              <div className="md:col-span-3">
                <Label>Зовнішній лікар <span className="text-rose-500">*</span></Label>
                <SearchableSelect
                  value={form.external_doctor_id}
                  onChange={(v) => setForm({ ...form, external_doctor_id: v })}
                  options={extDoctors.map((d) => ({
                    value: d.id, label: d.full_name,
                    hint: [d.speciality, d.clinic, d.city].filter(Boolean).join(" · "),
                    keywords: [d.full_name, d.phone, d.speciality, d.clinic, d.city].filter(Boolean).join(" "),
                  }))}
                  placeholder="Оберіть зовнішнього лікаря з довідника"
                  empty="Лікарів не знайдено. Додайте у Налаштуваннях → Зовнішні лікарі."
                  testid="rec-ext-doctor"
                />
              </div>
            )}

            <div className="md:col-span-3 mt-2 p-3 border border-slate-200 rounded-md bg-slate-50/30">
              <Label className="text-sm font-semibold text-[#0F2027]">Персонал клініки</Label>
              <div className="text-xs text-slate-500 mb-2">Лікар / менеджер / продавець / фельдшер(и) клініки. Отримує нарахування за роботу з пацієнтом.</div>
              <TeamEditor title="" team={form.clinic_team} empByRole={empByRoleClinic}
                onAdd={() => addTeam("clinic_team")} onRemove={(i) => rmTeam("clinic_team", i)}
                onUpdate={(i, k, v) => updTeam("clinic_team", i, k, v)} testid="clinic" />
            </div>

            {isReferred && currentSrcNeedsTeam && (
              <div className="md:col-span-3 mt-2 p-3 border-2 border-dashed border-[#0D9488] rounded-md bg-teal-50/30" data-testid="trip-team-block">
                <Label className="text-sm font-semibold text-[#0F2027]">Бригада направлення (обов&apos;язково для «{currentSrc?.label}»)</Label>
                <div className="text-xs text-slate-500 mb-2">Лікар / менеджер / продавець / фельдшер(и). Отримує нарахування за направлення.</div>
                <TeamEditor title="" team={form.business_trip_team} empByRole={empByRoleTrip}
                  onAdd={() => addTeam("business_trip_team")} onRemove={(i) => rmTeam("business_trip_team", i)}
                  onUpdate={(i, k, v) => updTeam("business_trip_team", i, k, v)} testid="trip" />
                <Label className="text-xs mt-2 block">Коментар бригади</Label>
                <Input value={form.business_trip_comment} onChange={(e) => setForm({ ...form, business_trip_comment: e.target.value })} />
              </div>
            )}

            <div className="md:col-span-3 mt-2">
              <Label className="text-sm font-semibold text-[#0F2027]">Діагноз <span className="text-rose-500">*</span></Label>
              <textarea
                data-testid="rec-diagnosis"
                value={form.diagnosis}
                onChange={(e) => setForm({ ...form, diagnosis: e.target.value })}
                placeholder="напр. Незріла стареча катаракта OD, амбліопія, супутні захворювання…"
                rows={4}
                className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0D9488]/30 resize-y"
              />
            </div>

            <div className="md:col-span-3">
              <Label className="text-sm font-semibold text-[#0F2027]">Рекомендовано</Label>
              <textarea
                data-testid="rec-recommended-text"
                value={form.recommended_text || ""}
                onChange={(e) => setForm({ ...form, recommended_text: e.target.value })}
                placeholder="Реальні рекомендації лікаря з медичної виписки (для пацієнта та персоналу). Це поле НЕ впливає на зарплату та операційний список."
                rows={3}
                className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0D9488]/30 resize-y"
              />
              <div className="text-xs text-slate-500 mt-1">Інформаційне поле — не бере участі у розрахунках і не переноситься в операційний список.</div>
            </div>

            <div className="md:col-span-3"><Label>Коментар</Label>
              <Input value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} /></div>

            <div>
              <Label>Статус</Label>
              <Select value={form.lifecycle_status || "new"} onValueChange={(v) => setForm({ ...form, lifecycle_status: v })}>
                <SelectTrigger data-testid="rec-lifecycle"><SelectValue /></SelectTrigger>
                <SelectContent>{LIFECYCLES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Відповідальний менеджер</Label>
              <Select value={form.manager_id || "none"} onValueChange={(v) => setForm({ ...form, manager_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="rec-manager"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Не призначено —</SelectItem>
                  {employees.filter((e) => e.role === "manager").map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-3">
              <Label>Останній коментар (для модуля «Дзвінки»)</Label>
              <Input data-testid="rec-last-comment" value={form.last_comment || ""} onChange={(e) => setForm({ ...form, last_comment: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Скасувати</Button>
            <Button onClick={save} data-testid="save-rec" disabled={saving} className="bg-[#0F2027]">
              {saving ? (<><span className="inline-block h-4 w-4 mr-2 border-2 border-white/70 border-t-transparent rounded-full animate-spin" />Збереження…</>) : "Зберегти"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iteration 28: Recommendation detail dialog with per-item execution */}
      <Dialog open={!!detailRec} onOpenChange={(v) => { if (!v) setDetailRec(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {detailRec && (() => {
            const pat = patients.find((p) => p.id === detailRec.patient_id);
            const src = srcByKey[detailRec.referral_source];
            const ex = detailRec.execution || { items: [], done_count: 0, total_count: 0, status: "not_started", status_label: "—" };
            const ExIcon = EXEC_ICON[ex.status] || Clock;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <span>Рекомендація · {pat?.full_name || "—"}</span>
                    <Badge className={`${EXEC_BADGE[ex.status]} hover:${EXEC_BADGE[ex.status]} inline-flex items-center gap-1`}>
                      <ExIcon className="h-3 w-3" /> {ex.status_label} · {ex.done_count}/{ex.total_count}
                    </Badge>
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 text-sm">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div><span className="text-slate-500">Створено:</span> {(detailRec.created_at || "").slice(0, 10)}</div>
                    <div><span className="text-slate-500">Термін дії до:</span> {(detailRec.expires_at || "").slice(0, 10)}</div>
                    <div><span className="text-slate-500">Залишилось днів:</span> {typeof ex.days_left === "number" ? (ex.days_left > 0 ? ex.days_left : `протерм. на ${-ex.days_left}`) : "—"}</div>
                    <div><span className="text-slate-500">Тип пацієнта:</span> {detailRec.patient_type === "primary" ? "Первинний" : "Направлений"}</div>
                    <div><span className="text-slate-500">Джерело:</span> {src?.label || detailRec.referral_source || "—"}</div>
                    <div><span className="text-slate-500">Менеджер:</span> {employees.find((e) => e.id === detailRec.manager_id)?.full_name || "—"}</div>
                  </div>
                  {detailRec.diagnosis && (
                    <div>
                      <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Діагноз</div>
                      <div className="p-2 bg-slate-50 border border-slate-200 rounded text-xs whitespace-pre-wrap">{detailRec.diagnosis}</div>
                    </div>
                  )}
                  {detailRec.recommended_text && (
                    <div>
                      <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Рекомендовано</div>
                      <div className="p-2 bg-slate-50 border border-slate-200 rounded text-xs whitespace-pre-wrap">{detailRec.recommended_text}</div>
                    </div>
                  )}
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Пункти рекомендації</div>
                    <div className="space-y-2">
                      {(ex.items || []).map((it, idx) => (
                        <div key={idx}
                              className={`p-3 border rounded ${it.execution_status === "completed" ? "bg-emerald-50/40 border-emerald-200" : "bg-white border-slate-200"}`}
                              data-testid={`rec-detail-item-${idx}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                              <div className="font-medium text-[#0F2027]">
                                {it.procedure_name || "—"}
                                {it.eye && <Badge variant="outline" className="ml-2 text-[10px]">{it.eye}</Badge>}
                              </div>
                              {it.operation && (
                                <div className="mt-1 text-xs text-slate-600 space-y-0.5">
                                  <div>Дата операції: <b>{it.operation.date}</b>
                                    {it.operation.operation_day_id && (
                                      <a
                                        href={`/operational-days/${it.operation.operation_day_id}#op-${it.operation.operation_id}`}
                                        className="ml-2 text-[#0D9488] hover:underline"
                                        data-testid={`rec-detail-op-link-${idx}`}
                                      >відкрити день</a>
                                    )}
                                  </div>
                                  {it.operation.surgeon_name && <div>Хірург: {it.operation.surgeon_name}</div>}
                                  <div>Сума: <b>{(it.operation.total_amount || 0).toLocaleString("uk-UA")} ₴</b> · Сплачено: {(it.operation.paid_amount || 0).toLocaleString("uk-UA")} ₴ · Борг: <span className="text-red-600">{(it.operation.debt || 0).toLocaleString("uk-UA")} ₴</span></div>
                                </div>
                              )}
                            </div>
                            <div className="text-right space-y-2">
                              <Badge className={`${ITEM_BADGE[it.execution_status]} hover:${ITEM_BADGE[it.execution_status]}`}>
                                {it.execution_status_label}
                              </Badge>
                              {["not_started", "expired"].includes(it.execution_status) && (
                                <div>
                                  <a
                                    href={`/operational-days`}
                                    className="text-[11px] text-[#0D9488] hover:underline"
                                    data-testid={`rec-schedule-item-${idx}`}
                                  >Записати в операційний день →</a>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      {(!ex.items || ex.items.length === 0) && (
                        <div className="text-sm text-slate-400">Пункти не вказано</div>
                      )}
                    </div>
                  </div>
                  {ex.pending_second_eye && (
                    <div className="p-3 bg-teal-50 border border-teal-200 rounded flex items-start gap-2">
                      <Eye className="h-5 w-5 text-[#0D9488] mt-0.5" />
                      <div className="text-xs">
                        <div className="font-semibold text-[#0F2027]">Очікує операції другого ока</div>
                        <div className="text-slate-600">Рекомендація містить процедуру для OD та OS, але виконана лише на одному оці.</div>
                      </div>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDetailRec(null)}>Закрити</Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TeamEditor({ title, team, empByRole, onAdd, onRemove, onUpdate, testid }) {
  return (
    <div className="md:col-span-3">
      {title && <div className="flex items-center justify-between mb-1">
        <Label className="text-sm font-semibold text-[#0F2027]">{title}</Label>
        <Button size="sm" variant="outline" onClick={onAdd} data-testid={`add-${testid}-member`}>
          <Plus className="h-3 w-3 mr-1" />Додати
        </Button>
      </div>}
      {!title && <Button size="sm" variant="outline" onClick={onAdd} data-testid={`add-${testid}-member`} className="mb-2">
        <Plus className="h-3 w-3 mr-1" />Додати учасника
      </Button>}
      <div className="space-y-2">
        {team.map((p, idx) => (
          <div key={idx} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_120px_40px] gap-2 items-end p-2 border border-slate-200 rounded-md bg-white">
            <div>
              <Label className="text-xs">Роль</Label>
              <Select value={p.role} onValueChange={(v) => onUpdate(idx, "role", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_L[r]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Працівник</Label>
              <Select value={p.employee_id} onValueChange={(v) => onUpdate(idx, "employee_id", v)}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{(empByRole[p.role] || []).map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Участь %</Label>
              <Input type="number" value={p.share_percent} onChange={(e) => onUpdate(idx, "share_percent", parseFloat(e.target.value || 0))} />
            </div>
            <Button size="icon" variant="ghost" onClick={() => onRemove(idx)}><X className="h-4 w-4 text-red-500" /></Button>
          </div>
        ))}
        {team.length === 0 && <div className="text-xs text-slate-400">Учасники не додані</div>}
      </div>
    </div>
  );
}
